/**
 * Dynamic Font Loader Service
 * Manages loading fonts on-demand from the API
 */

import * as opentype from 'opentype.js'

type FontLoadStatus = 'loading' | 'loaded' | 'error'

type LoadedFont = {
  status: FontLoadStatus
  error?: string
}

const createFontLoaderService = () => {
  const loadedFonts: Map<string, LoadedFont> = new Map()
  const loadingPromises: Map<string, Promise<void>> = new Map()
  // Codepoints supported by each font, keyed by font name. Shared across weights/italics
  // of the same family — supported glyphs are effectively identical across variants.
  const supportedCodepointsByFont: Map<string, Set<number>> = new Map()

  /**
   * Get font key for caching
   */
  const getFontKey = (fontName: string, weight: number, italic: boolean): string => {
    return `${fontName}-${weight}-${italic ? 'italic' : 'normal'}`
  }

  /**
   * Check if a font is already loaded
   */
  const isFontLoaded = (
    fontName: string,
    weight: number = 400,
    italic: boolean = false,
  ): boolean => {
    const key = getFontKey(fontName, weight, italic)
    const font = loadedFonts.get(key)
    return font?.status === 'loaded'
  }

  /**
   * Get the status of a font
   */
  const getFontStatus = (fontName: string): FontLoadStatus | null => {
    return loadedFonts.get(fontName)?.status || null
  }

  /**
   * Convert Base64 font data to a Uint8Array
   */
  const base64ToBytes = (base64: string): Uint8Array => {
    const byteCharacters = atob(base64)
    const byteNumbers = new Array(byteCharacters.length)

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }

    return new Uint8Array(byteNumbers)
  }

  /**
   * Parse a font binary and store the codepoints it supports.
   * Cached per font name so subsequent variants (weights/italics) skip the parse.
   */
  const extractSupportedCodepoints = (fontName: string, buffer: ArrayBuffer): void => {
    if (supportedCodepointsByFont.has(fontName)) return

    try {
      const parsed = opentype.parse(buffer)
      const codepoints = new Set<number>()

      // Primary source: the cmap table maps Unicode codepoints → glyph IDs.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cmapMap = (parsed.tables as any)?.cmap?.glyphIndexMap as
        | Record<string, number>
        | undefined
      if (cmapMap) {
        for (const codepoint in cmapMap) {
          codepoints.add(Number(codepoint))
        }
      }

      // Fallback / supplement: walk the glyph list and read each glyph's unicode(s).
      const glyphCount = parsed.glyphs.length
      for (let i = 0; i < glyphCount; i++) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const glyph = parsed.glyphs.get(i) as any
        if (!glyph) continue
        if (typeof glyph.unicode === 'number') codepoints.add(glyph.unicode)
        if (Array.isArray(glyph.unicodes)) {
          for (const cp of glyph.unicodes) codepoints.add(cp)
        }
      }

      supportedCodepointsByFont.set(fontName, codepoints)
    } catch (error) {
      console.warn(`Could not parse font ${fontName} for glyph coverage:`, error)
    }
  }

  /**
   * Perform the actual font loading
   */
  const performFontLoad = async (
    fontName: string,
    fontUrl: string,
    weight: number = 400,
    italic: boolean = false,
  ): Promise<void> => {
    const key = getFontKey(fontName, weight, italic)
    loadedFonts.set(key, { status: 'loading' })

    try {
      // Fetch the font data
      const response = await fetch(fontUrl)
      const fontData = await response.text()

      // Check if the response is Base64 encoded
      // If it starts with typical font file headers in Base64, it's likely Base64
      const isBase64 = /^[A-Za-z0-9+/]+={0,2}$/.test(fontData.trim())

      let fontBuffer: ArrayBuffer

      if (isBase64) {
        const bytes = base64ToBytes(fontData.trim())
        // Slice to a plain ArrayBuffer (TypedArray.buffer can be SharedArrayBuffer in some envs)
        fontBuffer = bytes.buffer.slice(
          bytes.byteOffset,
          bytes.byteOffset + bytes.byteLength,
        ) as ArrayBuffer
      } else {
        // Response was a direct URL pointer; fetch the binary
        const binaryResponse = await fetch(fontUrl)
        fontBuffer = await binaryResponse.arrayBuffer()
      }

      // Create FontFace directly from the buffer (no blob URL needed)
      const fontFace = new FontFace(fontName, fontBuffer, {
        style: italic ? 'italic' : 'normal',
        weight: weight.toString(),
        display: 'swap',
      })

      // Load the font
      await fontFace.load()

      // Add to document fonts
      document.fonts.add(fontFace)

      // Mark as loaded
      loadedFonts.set(key, { status: 'loaded' })

      // Parse the font for glyph coverage (cached per font name, no-op on repeats)
      extractSupportedCodepoints(fontName, fontBuffer)
    } catch (error) {
      console.error(
        `Failed to load font ${fontName} (weight: ${weight}, italic: ${italic}):`,
        error,
      )
      loadedFonts.set(key, {
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
      })
      throw error
    }
  }

  /**
   * Load a font dynamically
   */
  const loadFont = async (
    fontName: string,
    fontUrl: string,
    weight: number = 400,
    italic: boolean = false,
  ): Promise<void> => {
    const key = getFontKey(fontName, weight, italic)

    // If already loaded, return immediately
    if (isFontLoaded(fontName, weight, italic)) {
      return
    }

    // If currently loading, return the existing promise
    const existingPromise = loadingPromises.get(key)
    if (existingPromise) {
      return existingPromise
    }

    // Start loading the font
    const loadPromise = performFontLoad(fontName, fontUrl, weight, italic)
    loadingPromises.set(key, loadPromise)

    try {
      await loadPromise
    } finally {
      loadingPromises.delete(key)
    }
  }

  /**
   * Preload multiple fonts
   */
  const preloadFonts = async (
    fonts: Array<{ name: string; url: string; weight?: number; italic?: boolean }>,
  ): Promise<void> => {
    const promises = fonts.map((font) =>
      loadFont(font.name, font.url, font.weight || 400, font.italic || false).catch(() => {
        // Silently catch errors for preloading
        console.warn(`Failed to preload font: ${font.name} (weight: ${font.weight || 400})`)
      }),
    )

    await Promise.all(promises)
  }

  /**
   * Clear all loaded fonts (useful for cleanup)
   */
  const clear = (): void => {
    // Note: We cannot remove fonts from document.fonts in all browsers
    // So we just clear our tracking
    loadedFonts.clear()
    loadingPromises.clear()
    supportedCodepointsByFont.clear()
  }

  /**
   * Get the set of codepoints supported by a font.
   * Returns undefined if the font hasn't been loaded/parsed yet, or if parsing failed
   * (in which case callers should treat it as "unknown" and skip validation).
   */
  const getSupportedCodepoints = (fontName: string): Set<number> | undefined => {
    return supportedCodepointsByFont.get(fontName)
  }

  /**
   * Convenience helper: returns true if the codepoint is supported, OR if we have no
   * coverage data for this font (e.g. system fonts like Arial that we never parse).
   */
  const isCharSupported = (fontName: string, char: string): boolean => {
    const supported = supportedCodepointsByFont.get(fontName)
    if (!supported) return true
    const cp = char.codePointAt(0)
    if (cp === undefined) return true
    return supported.has(cp)
  }

  return {
    isFontLoaded,
    getFontStatus,
    loadFont,
    preloadFonts,
    clear,
    getSupportedCodepoints,
    isCharSupported,
  }
}

// Export singleton instance
export const fontLoader = createFontLoaderService()

// Hook for React components
import { useEffect, useState } from 'react'

export const useFontLoader = (
  fontName: string | null,
  fontUrl: string | null,
  weight: number = 400,
  italic: boolean = false,
) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!fontName || !fontUrl) {
      setIsLoaded(false)
      setIsLoading(false)
      setError(null)
      return
    }

    // Check if already loaded
    if (fontLoader.isFontLoaded(fontName, weight, italic)) {
      setIsLoaded(true)
      setIsLoading(false)
      return
    }

    // Load the font
    setIsLoading(true)
    setError(null)

    fontLoader
      .loadFont(fontName, fontUrl, weight, italic)
      .then(() => {
        setIsLoaded(true)
        setIsLoading(false)
      })
      .catch((err) => {
        setError(err.message)
        setIsLoading(false)
        setIsLoaded(false)
      })
  }, [fontName, fontUrl, weight, italic])

  return { isLoaded, isLoading, error }
}
